import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

interface EdgeInfo {
  segments: THREE.Line[];  // Multiple segments instead of a single line
  fromNode: number;
  toNode: number;
  layerConnection: string; // e.g., "fc1-fc2"
  weight?: number;
  fromPosition: THREE.Vector3;
  toPosition: THREE.Vector3;
}

export default class ThreeNetworkRendererClass {
  private container: HTMLElement;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private controls: OrbitControls;
  private nodes: THREE.Mesh[] = [];
  private edges: EdgeInfo[] = [];
  private animationSpeed = 1.0;
  private layerNodeIndices: Map<string, number[]> = new Map(); // Maps layer names to node indices
  
  // Animation state properties
  private isAnimating = false;
  private animationStartTime = 0;
  private currentTrainingData: any = null;
  private layerOrder: string[] = []; // Store layer names in order
  private animationDuration = 1000; // Total animation duration in ms
  private endAnimationDelay = 500; // Time to pause at the end of animation before requesting next epoch (ms)
  private isWaitingForNextEpoch = false; // Flag to track if we're in the final delay period
  private isBackpropMode = false; // Flag to track if we're in backprop animation mode
  private backpropStartTime = 0; // Time when backprop animation started

  constructor(container: HTMLElement) {
    this.container = container;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x1a1a1a); // Dark gray background instead of black
    this.camera = new THREE.PerspectiveCamera(
      75,
      container.clientWidth / container.clientHeight,
      0.1,
      1000
    );
    this.camera.position.z = 10;

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(this.renderer.domElement);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.minDistance = 1;
    this.controls.maxDistance = 100;

    // Setup lighting - brighter for better visibility
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);
    const pointLight = new THREE.PointLight(0xffffff, 1, 200);
    pointLight.position.set(20, 20, 20);
    this.scene.add(pointLight);

    // Start animation loop
    this.animate();
  }

  public setNetworkStructure(structure: any) {
    // Clear existing nodes and edges
    this.nodes.forEach(node => this.scene.remove(node));
    this.edges.forEach(edge => edge.segments.forEach(segment => this.scene.remove(segment)));
    this.nodes = [];
    this.edges = [];
    this.layerNodeIndices.clear();
    this.layerOrder = []; // Clear layer order
    
    // Create new nodes based on layers
    const layerSpacing = 2;
    const nodeSpacing = 0.5;
    
    // Array to track layers and their nodes
    interface LayerInfo {
      id: number;
      nodes: THREE.Mesh[];
      nodeCount: number;
      isReLU: boolean;
      name?: string;
    }
    const allLayers: LayerInfo[] = [];
    
    // First pass: determine layer structure and node counts
    const visualLayers: {type: string, nodeCount: number, name?: string}[] = [];

    // Determine which layers need visual representation
    for (let i = 0; i < structure.layers.length; i++) {
      const layer = structure.layers[i];

      // Extract node count from parameters - different layer types use different names
      let nodeCount = 32; // default fallback

      if (layer.parameters) {
        // Try different parameter names used by different layer types
        nodeCount = layer.parameters.out_features ||     // Linear layers
                    layer.parameters.output_size ||      // Some models
                    layer.parameters.num_features ||     // Feature dimensions
                    layer.parameters.filters ||          // Conv layers
                    layer.parameters.channels ||         // Channel count
                    32;                                  // fallback
      }

      // Include all layer types in visualization
      visualLayers.push({
        type: layer.name === "input" ? "input" :
              layer.name === "output" ? "output" : "hidden",
        nodeCount: nodeCount,
        name: layer.name
      });

      // Record layer name in order for animation
      if (layer.name) {
        this.layerOrder.push(layer.name);
      }
    }
    
    // Create nodes for each visual layer
    visualLayers.forEach((layer, layerIndex) => {
      const x = layerIndex * layerSpacing - (visualLayers.length - 1) * layerSpacing / 2;
      
      const nodeCount = layer.nodeCount;
      const layerNodes: THREE.Mesh[] = [];
      const totalHeight = nodeCount * nodeSpacing;
      const startY = -totalHeight / 2;
      
      const nodeIndices: number[] = [];
      
      for (let j = 0; j < nodeCount; j++) {
        const y = startY + j * nodeSpacing;
        const node = this.createNode(new THREE.Vector3(x, y, 0));
        node.name = layer.name || `layer-${layerIndex}`; // Assign layer name to the node
        
        // Store the index of this node in the nodes array
        const nodeIndex = this.nodes.length;
        nodeIndices.push(nodeIndex);
        
        this.nodes.push(node);
        layerNodes.push(node);
        this.scene.add(node);
      }

      // Add node indices to the map
      if (layer.name) {
        this.layerNodeIndices.set(layer.name, nodeIndices);
      }

      allLayers.push({
        id: layerIndex,
        nodes: layerNodes,
        nodeCount: nodeCount,
        isReLU: false,
        name: layer.name
      });
    });

    // Create connections between consecutive visual layers
    for (let i = 0; i < allLayers.length - 1; i++) {
      const fromLayer = allLayers[i];
      const toLayer = allLayers[i + 1];
      
      // Only create connections between named layers (we need names for weight mapping)
      if (fromLayer.name && toLayer.name) {
        const connectionName = `${fromLayer.name}-${toLayer.name}`;
        
        for (let fromIdx = 0; fromIdx < fromLayer.nodes.length; fromIdx++) {
          const fromNode = fromLayer.nodes[fromIdx];
          const fromNodeIndex = this.layerNodeIndices.get(fromLayer.name)?.[fromIdx] || 0;
          
          for (let toIdx = 0; toIdx < toLayer.nodes.length; toIdx++) {
            const toNode = toLayer.nodes[toIdx];
            const toNodeIndex = this.layerNodeIndices.get(toLayer.name)?.[toIdx] || 0;
            
            const edgeSegments = this.createEdge(fromNode.position, toNode.position);
            
            // Store the edge with its connection information
            this.edges.push({
              segments: edgeSegments,
              fromNode: fromNodeIndex,
              toNode: toNodeIndex,
              layerConnection: connectionName,
              fromPosition: fromNode.position,
              toPosition: toNode.position
            });
          }
        }
      }
    }

    // Fit camera to all nodes
    this.fitCameraToNodes();
    console.log(`[3D VIZ] Created network with ${this.nodes.length} nodes and ${this.edges.length} edges`);
    console.log(`[3D VIZ] Layer details:`, visualLayers.map(l => ({ name: l.name, type: l.type, nodeCount: l.nodeCount })));
  }

  // Fit camera to view all nodes in the scene
  private fitCameraToNodes() {
    if (this.nodes.length === 0) {
      this.camera.position.z = 10;
      return;
    }

    // Calculate bounding box of all nodes
    const box = new THREE.Box3();
    this.nodes.forEach(node => {
      box.expandByObject(node);
    });

    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = this.camera.fov * (Math.PI / 180); // convert vertical fov to radians
    let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));

    // Add padding
    cameraZ *= 1.5;

    const center = box.getCenter(new THREE.Vector3());
    this.camera.position.set(center.x, center.y, center.z + cameraZ);
    this.controls.target.copy(center);
    this.controls.update();
  }

  public updateTrainingData(data: any) {
    // Store the new training data
    this.currentTrainingData = data;
    
    // Reset all nodes and edges to base state (gray)
    this.resetVisualizationState();
    
    // Start animation for this training data
    this.isAnimating = true;
    this.animationStartTime = performance.now();
  }

  // Reset all nodes and edges to base (gray) state
  private resetVisualizationState() {
    // Reset all nodes to gray
    this.nodes.forEach(node => {
      const material = node.material as THREE.MeshStandardMaterial;
      material.emissive = new THREE.Color(0x444444);
      material.emissiveIntensity = 0.1;
    });
    
    // Reset all edges to gray
    this.edges.forEach(edge => {
      edge.segments.forEach(segment => {
        const material = segment.material as THREE.LineBasicMaterial;
        material.color = new THREE.Color(0x888888);
        material.opacity = 0.2;
      });
    });
  }

  // Process animation each frame
  private processAnimation(currentTime: number) {
    if (!this.isAnimating || !this.currentTrainingData) return;
    
    const elapsedTime = currentTime - this.animationStartTime;
    const totalDuration = this.animationDuration + this.endAnimationDelay;
    
    // Check if we're in the delay phase at the end of the animation
    if (elapsedTime >= this.animationDuration && !this.isWaitingForNextEpoch && !this.isBackpropMode) {
      // We've reached the end of the regular animation, now switch to backprop mode
      this.isBackpropMode = true;
      this.backpropStartTime = currentTime;
      
      // Apply the final state of the forward pass
      this.applyFinalState();
      return;
    }
    
    // Check if we're in backprop mode
    if (this.isBackpropMode) {
      const backpropElapsedTime = currentTime - this.backpropStartTime;
      
      // If backprop finished, switch to waiting mode and prepare for next epoch
      if (backpropElapsedTime >= this.animationDuration) {
        this.isWaitingForNextEpoch = true;
        this.isBackpropMode = false;
      } else {
        // Process backprop animation
        this.processBackpropAnimation(backpropElapsedTime);
      }
      
      // Check if the entire animation cycle (forward + backprop + delay) is complete
      if (this.isWaitingForNextEpoch && currentTime - this.backpropStartTime >= this.animationDuration + this.endAnimationDelay) {
        this.isAnimating = false;
        this.isWaitingForNextEpoch = false;
      }
      
      return;
    }
    
    // Check if the entire animation cycle is complete (forward only, no backprop)
    if (elapsedTime >= totalDuration && !this.isBackpropMode) {
      this.isAnimating = false;
      this.isWaitingForNextEpoch = false;
      return;
    }
    
    // Only process the forward animation if we're not in any special phase
    if (elapsedTime < this.animationDuration && !this.isBackpropMode) {
      // Calculate which layers should be active based on elapsed time
      // The animation progresses through layers in sequence
      const progress = elapsedTime / this.animationDuration;
      const layerCount = this.layerOrder.length;
      
      // Calculate which layer is currently active and the progress within that layer
      // We divide the total animation into layerCount segments
      const segmentLength = 1.0 / layerCount;
      const activeLayerIndex = Math.min(Math.floor(progress / segmentLength), layerCount - 1);
      
      // Calculate progress within the current layer (0-1)
      const layerProgress = (progress - (activeLayerIndex * segmentLength)) / segmentLength;
      
      // Activate all layers up to and including the active layer
      this.animateLayersSequentially(activeLayerIndex, layerProgress);
    }
  }
  
  // Process backpropagation animation
  private processBackpropAnimation(elapsedTime: number) {
    // Calculate progress and active layer for backprop (right to left)
    const progress = elapsedTime / this.animationDuration;
    const layerCount = this.layerOrder.length;
    
    // For backprop, we go in reverse order (right to left)
    const segmentLength = 1.0 / layerCount;
    // Calculate the reverse index (from right to left)
    const activeLayerIndex = layerCount - 1 - Math.min(Math.floor(progress / segmentLength), layerCount - 1);
    
    // Calculate progress within the current layer (0-1)
    const layerProgress = (progress - ((layerCount - 1 - activeLayerIndex) * segmentLength)) / segmentLength;
    
    // Animate backprop through layers from right to left
    this.animateBackpropSequentially(activeLayerIndex, layerProgress);
  }
  
  // Animate backprop layers sequentially (right to left)
  private animateBackpropSequentially(activeLayerIndex: number, layerProgress: number) {
    const data = this.currentTrainingData;
    if (!data || !data.activations) return;
    
    const activationsMap = new Map<string, number[]>();
    
    // Collect all activations
    Object.entries(data.activations).forEach(([layerName, values]) => {
      if (Array.isArray(values)) {
        activationsMap.set(layerName, values as number[]);
      }
    });
    
    // Track node activation progress for all layers in backprop
    const nodeActivationProgress = new Map<string, Map<number, number>>();
    
    // First, fully activate all layers to the right of active layer (backprop has already passed through them)
    for (let i = this.layerOrder.length - 1; i > activeLayerIndex; i--) {
      const layerName = this.layerOrder[i];
      this.animateLayerNodesBackprop(layerName, activationsMap, 1.0);
      
      // Fully activate connections between these right-side layers
      if (i < this.layerOrder.length - 1) {
        const nextLayerName = this.layerOrder[i + 1]; // Layer to the right
        this.animateLayerConnectionsBackprop(layerName, nextLayerName, activationsMap, 1.0);
      }
    }
    
    // Now handle the active layer's nodes - they're always fully activated
    const currentLayerName = this.layerOrder[activeLayerIndex];
    this.animateLayerNodesBackprop(currentLayerName, activationsMap, 1.0);
    
    // Handle connections from active layer to the layer on its left
    if (activeLayerIndex > 0) {
      const prevLayerName = this.layerOrder[activeLayerIndex - 1];
      
      // Initialize node activation for the previous layer if needed
      if (!nodeActivationProgress.has(prevLayerName)) {
        nodeActivationProgress.set(prevLayerName, new Map<number, number>());
        const indices = this.layerNodeIndices.get(prevLayerName) || [];
        indices.forEach(idx => {
          nodeActivationProgress.get(prevLayerName)?.set(idx, 0);
        });
      }
      
      // Animate connections from current layer to previous layer (backprop direction)
      const connectionKey = `${prevLayerName}-${currentLayerName}`;
      
      // Use a consistent signal speed for backprop too
      const signalSpeed = 4.0; // Units per second
      // Calculate time elapsed for this layer segment
      const layerTime = this.animationDuration / this.layerOrder.length; // Time for one layer
      const timeElapsed = layerProgress * layerTime; // ms
      const timeInSeconds = timeElapsed / 1000;
      const maxDistance = timeInSeconds * signalSpeed;
      
      // Now animate the connections and update previous layer's nodes
      this.edges.forEach(edge => {
        // In backprop, we need to use the connection but visualize signal flowing in reverse
        if (edge.layerConnection === connectionKey) {
          // Get connection data
          if (!data?.weights || !activationsMap.has(currentLayerName)) return;
          
          const weightKey = `${currentLayerName}.weight`;
          if (!data.weights[weightKey]) return;
          
          const fromActivations = activationsMap.get(prevLayerName); // From is now the left layer 
          const toActivations = activationsMap.get(currentLayerName); // To is now the right layer
          
          if (!fromActivations || !toActivations) return;
          
          const fromLayerIndices = this.layerNodeIndices.get(prevLayerName) || [];
          const toLayerIndices = this.layerNodeIndices.get(currentLayerName) || [];
          
          // For backprop, we're flowing in reverse direction (toNode -> fromNode)
          // But the edge is still defined from left to right
          const fromLayerIndex = fromLayerIndices.indexOf(edge.fromNode);
          const toLayerIndex = toLayerIndices.indexOf(edge.toNode);
          
          if (fromLayerIndex < 0 || toLayerIndex < 0 || 
              !data.weights[weightKey][toLayerIndex] || 
              fromLayerIndex >= fromActivations.length) return;
          
          // Get the weight and activation (for backprop we use the activation of right layer node)
          const weight = data.weights[weightKey][toLayerIndex][fromLayerIndex];
          const activation = toActivations[toLayerIndex]; // Use the right side activation
          
          // For backprop, distance is the same but we visualize from right to left
          const distance = edge.fromPosition.distanceTo(edge.toPosition);
          // For backprop animation, we flip the progress calculation
          const connectionProgress = Math.min(1.0, maxDistance / distance);
          
          // Animate the connection segments in backprop style
          this.animateConnectionSegmentsBackprop(edge, weight, activation, connectionProgress);
          
          // If the backprop signal has reached the left node, update its activation
          if (connectionProgress >= 1.0) {
            // Get the current progress for this node
            const currentProgress = nodeActivationProgress.get(prevLayerName)?.get(edge.fromNode) || 0;
            
            // Each connection contributes to the node's activation
            const signalContribution = 0.1; // Adjust as needed
            const newProgress = Math.min(1.0, currentProgress + signalContribution);
            
            // Update the node's activation progress
            nodeActivationProgress.get(prevLayerName)?.set(edge.fromNode, newProgress);
          }
        }
      });
      
      // Now update the previous layer's nodes based on their activation progress
      this.nodes.forEach((node, i) => {
        if (node.name === prevLayerName) {
          // Get this node's progress
          const nodeProgress = nodeActivationProgress.get(prevLayerName)?.get(i) || 0;
          
          if (nodeProgress > 0) {
            // Only animate nodes that have received some backprop signal
            const layerActivations = activationsMap.get(prevLayerName);
            if (!layerActivations) return;
            
            // Find the index of this node within its layer
            const layerIndices = this.layerNodeIndices.get(prevLayerName) || [];
            const nodeIndex = layerIndices.indexOf(i);
            
            if (nodeIndex >= 0 && nodeIndex < layerActivations.length) {
              const activation = layerActivations[nodeIndex];
              const minActivation = Math.min(...layerActivations);
              const maxActivation = Math.max(...layerActivations);
              const gradient = data?.gradients?.[i] || 0;
              
              // Update the node appearance with backprop styling
              this.updateNodeAppearanceBackprop(
                node,
                activation,
                gradient,
                minActivation,
                maxActivation,
                nodeProgress
              );
            }
          }
        }
      });
    }
  }
  
  // Helper to animate connections for backprop
  private animateLayerConnectionsBackprop(fromLayerName: string, toLayerName: string, activationsMap: Map<string, number[]>, progress: number) {
    const connectionKey = `${fromLayerName}-${toLayerName}`;
    const data = this.currentTrainingData;
    
    if (!data?.weights || !activationsMap.has(fromLayerName)) return;
    
    this.edges.forEach(edge => {
      if (edge.layerConnection === connectionKey) {
        const weightKey = `${toLayerName}.weight`;
        if (!data.weights[weightKey]) return;
        
        const fromActivations = activationsMap.get(fromLayerName);
        const fromLayerIndices = this.layerNodeIndices.get(fromLayerName) || [];
        const toLayerIndices = this.layerNodeIndices.get(toLayerName) || [];
        
        const fromLayerIndex = fromLayerIndices.indexOf(edge.fromNode);
        const toLayerIndex = toLayerIndices.indexOf(edge.toNode);
        
        if (fromLayerIndex < 0 || toLayerIndex < 0 || 
            !data.weights[weightKey][toLayerIndex] || 
            !fromActivations || fromLayerIndex >= fromActivations.length) return;
        
        const weight = data.weights[weightKey][toLayerIndex][fromLayerIndex];
        const activation = fromActivations[fromLayerIndex];
        
        // Fully illuminate the connection with backprop styling
        this.animateConnectionSegmentsBackprop(edge, weight, activation, progress);
      }
    });
  }
  
  // Animate connection segments for backprop (light blue color)
  private animateConnectionSegmentsBackprop(edge: EdgeInfo, weight: number, activation: number, progress: number) {
    const numSegments = edge.segments.length;
    
    // Calculate the "real" activation as the product of weight and activation
    const signalStrength = weight * activation;
    
    // Use the absolute value for brightness, but keep track of sign for color
    const absSignalStrength = Math.abs(signalStrength);
    
    // Scale to reasonable range
    const normalizedStrength = Math.min(absSignalStrength, 1.0);
    
    // Create color based on the sign of the signal (using light blue for backprop)
    // Light blue: 0x00BFFF for positive, darker blue 0x0000CC for negative
    const color = new THREE.Color(signalStrength >= 0 ? 0x00BFFF : 0x0000CC);
    
    // Calculate the total physical length of the connection
    const totalLength = edge.fromPosition.distanceTo(edge.toPosition);
    
    // For backprop, we animate from right to left, so reverse the progress
    // This means we need to flip the segment order
    const distanceTraveled = progress * totalLength;
    
    // Update each segment based on its physical position (reversed for backprop)
    edge.segments.forEach((segment, i) => {
      const material = segment.material as THREE.LineBasicMaterial;
      
      // Calculate the segment's start and end points
      const segmentLength = totalLength / numSegments;
      
      // For backprop, we reverse the segment order (animate right to left)
      const reversedIndex = numSegments - 1 - i;
      const segmentStart = segmentLength * reversedIndex;
      const segmentEnd = segmentLength * (reversedIndex + 1);
      
      // If the backprop signal has reached this segment
      if (distanceTraveled > segmentStart) {
        // Calculate how much of this segment should be lit
        const segmentProgress = Math.min(1.0, (distanceTraveled - segmentStart) / segmentLength);
        
        // Segments that should be illuminated with backprop color
        material.color = color;
        
        // Set opacity based on signal strength and how far the signal has traveled into this segment
        const opacity = (0.1 + normalizedStrength * 0.9) * segmentProgress;
        material.opacity = opacity;
      } else {
        // Segments that should remain unilluminated (gray)
        material.color = new THREE.Color(0x888888);
        material.opacity = 0.2;
      }
    });
  }
  
  // Helper method to animate nodes in a layer for backprop (blue coloring)
  private animateLayerNodesBackprop(layerName: string, activationsMap: Map<string, number[]>, progress: number) {
    if (!activationsMap.has(layerName)) return;
    
    const layerActivations = activationsMap.get(layerName)!;
    const minActivation = Math.min(...layerActivations);
    const maxActivation = Math.max(...layerActivations);
    
    this.nodes.forEach((node, i) => {
      if (node.name === layerName) {
        const layerIndices = this.layerNodeIndices.get(layerName) || [];
        const nodeIndex = layerIndices.indexOf(i);
        
        if (nodeIndex >= 0 && nodeIndex < layerActivations.length) {
          const activation = layerActivations[nodeIndex];
          const gradient = this.currentTrainingData?.gradients?.[i] || 0;
          
          this.updateNodeAppearanceBackprop(
            node,
            activation,
            gradient,
            minActivation,
            maxActivation,
            progress
          );
        }
      }
    });
  }
  
  // Update node appearance for backprop animation (blue color scheme)
  private updateNodeAppearanceBackprop(node: THREE.Mesh, activation: number, gradient: number, minActivation: number, maxActivation: number, progress: number = 1.0) {
    const material = node.material as THREE.MeshStandardMaterial;
    const color = new THREE.Color(0x00BFFF); // Light blue color for backprop

    // Normalize the activation value to the range 0-1
    const normalizedActivation = (activation - minActivation) / (maxActivation - minActivation);

    // Set a minimum brightness
    let brightness = 0.1;

    // Increase brightness based on normalized activation, scaled by progress
    brightness += Math.min(Math.abs(normalizedActivation), 1) * 0.9 * progress;

    material.emissive = color;
    material.emissiveIntensity = brightness;
  }

  // Animate layers sequentially but allow nodes to light up when connections reach them
  private animateLayersSequentially(activeLayerIndex: number, layerProgress: number) {
    const data = this.currentTrainingData;
    if (!data || !data.activations) return;
    
    const activationsMap = new Map<string, number[]>();
    
    // Collect all activations
    Object.entries(data.activations).forEach(([layerName, values]) => {
      if (Array.isArray(values)) {
        activationsMap.set(layerName, values as number[]);
      }
    });
    
    // Track node activation progress for all layers
    const nodeActivationProgress = new Map<string, Map<number, number>>();
    
    // First, fully activate all previous layers
    for (let i = 0; i < activeLayerIndex; i++) {
      const layerName = this.layerOrder[i];
      this.animateLayerNodes(layerName, activationsMap, 1.0);
      
      // Fully activate connections between previous layers
      if (i < activeLayerIndex - 1) {
        const nextLayerName = this.layerOrder[i + 1];
        this.animateLayerConnections(layerName, nextLayerName, activationsMap, 1.0);
      }
    }
    
    // Now handle the active layer's nodes - they're always fully activated
    const currentLayerName = this.layerOrder[activeLayerIndex];
    this.animateLayerNodes(currentLayerName, activationsMap, 1.0);
    
    // Handle connections from the active layer to the next layer
    if (activeLayerIndex < this.layerOrder.length - 1) {
      const nextLayerName = this.layerOrder[activeLayerIndex + 1];
      
      // Initialize node activation for the next layer if needed
      if (!nodeActivationProgress.has(nextLayerName)) {
        nodeActivationProgress.set(nextLayerName, new Map<number, number>());
        const indices = this.layerNodeIndices.get(nextLayerName) || [];
        indices.forEach(idx => {
          nodeActivationProgress.get(nextLayerName)?.set(idx, 0);
        });
      }
      
      // Animate connections from current to next layer with current progress
      const connectionKey = `${currentLayerName}-${nextLayerName}`;
      
      // Use a consistent signal speed
      const signalSpeed = 4.0; // Units per second
      // Calculate time elapsed for this layer segment
      const layerTime = this.animationDuration / this.layerOrder.length; // Time for one layer
      const timeElapsed = layerProgress * layerTime; // ms
      const timeInSeconds = timeElapsed / 1000;
      const maxDistance = timeInSeconds * signalSpeed;
      
      // Now animate the connections and update next layer's nodes
      this.edges.forEach(edge => {
        if (edge.layerConnection === connectionKey) {
          // Get connection data
          if (!data?.weights || !activationsMap.has(currentLayerName)) return;
          
          const weightKey = `${nextLayerName}.weight`;
          if (!data.weights[weightKey]) return;
          
          const fromActivations = activationsMap.get(currentLayerName);
          const fromLayerIndices = this.layerNodeIndices.get(currentLayerName) || [];
          const toLayerIndices = this.layerNodeIndices.get(nextLayerName) || [];
          
          const fromLayerIndex = fromLayerIndices.indexOf(edge.fromNode);
          const toLayerIndex = toLayerIndices.indexOf(edge.toNode);
          
          if (fromLayerIndex < 0 || toLayerIndex < 0 || 
              !data.weights[weightKey][toLayerIndex] || 
              !fromActivations || fromLayerIndex >= fromActivations.length) return;
          
          // Get the weight and activation
          const weight = data.weights[weightKey][toLayerIndex][fromLayerIndex];
          const activation = fromActivations[fromLayerIndex];
          
          // Calculate how far along this connection the signal has traveled
          const distance = edge.fromPosition.distanceTo(edge.toPosition);
          const connectionProgress = Math.min(1.0, maxDistance / distance);
          
          // Animate the connection segments
          this.animateConnectionSegments(edge, weight, activation, connectionProgress);
          
          // If the signal has reached the destination node, update its activation
          if (connectionProgress >= 1.0) {
            // Get the current progress for this node
            const currentProgress = nodeActivationProgress.get(nextLayerName)?.get(edge.toNode) || 0;
            
            // Each connection contributes to the node's activation
            const signalContribution = 0.1; // Adjust as needed
            const newProgress = Math.min(1.0, currentProgress + signalContribution);
            
            // Update the node's activation progress
            nodeActivationProgress.get(nextLayerName)?.set(edge.toNode, newProgress);
          }
        }
      });
      
      // Now update the next layer's nodes based on their activation progress
      this.nodes.forEach((node, i) => {
        if (node.name === nextLayerName) {
          // Get this node's progress
          const nodeProgress = nodeActivationProgress.get(nextLayerName)?.get(i) || 0;
          
          if (nodeProgress > 0) {
            // Only animate nodes that have received some signal
            const layerActivations = activationsMap.get(nextLayerName);
            if (!layerActivations) return;
            
            // Find the index of this node within its layer
            const layerIndices = this.layerNodeIndices.get(nextLayerName) || [];
            const nodeIndex = layerIndices.indexOf(i);
            
            if (nodeIndex >= 0 && nodeIndex < layerActivations.length) {
              const activation = layerActivations[nodeIndex];
              const minActivation = Math.min(...layerActivations);
              const maxActivation = Math.max(...layerActivations);
              const gradient = data?.gradients?.[i] || 0;
              
              // Update the node appearance based on both activation and progress
              this.updateNodeAppearance(
                node,
                activation,
                gradient,
                minActivation,
                maxActivation,
                nodeProgress
              );
            }
          }
        }
      });
    }
  }
  
  // Helper to animate connections between two layers
  private animateLayerConnections(fromLayerName: string, toLayerName: string, activationsMap: Map<string, number[]>, progress: number) {
    const connectionKey = `${fromLayerName}-${toLayerName}`;
    const data = this.currentTrainingData;
    
    if (!data?.weights || !activationsMap.has(fromLayerName)) return;
    
    this.edges.forEach(edge => {
      if (edge.layerConnection === connectionKey) {
        const weightKey = `${toLayerName}.weight`;
        if (!data.weights[weightKey]) return;
        
        const fromActivations = activationsMap.get(fromLayerName);
        const fromLayerIndices = this.layerNodeIndices.get(fromLayerName) || [];
        const toLayerIndices = this.layerNodeIndices.get(toLayerName) || [];
        
        const fromLayerIndex = fromLayerIndices.indexOf(edge.fromNode);
        const toLayerIndex = toLayerIndices.indexOf(edge.toNode);
        
        if (fromLayerIndex < 0 || toLayerIndex < 0 || 
            !data.weights[weightKey][toLayerIndex] || 
            !fromActivations || fromLayerIndex >= fromActivations.length) return;
        
        const weight = data.weights[weightKey][toLayerIndex][fromLayerIndex];
        const activation = fromActivations[fromLayerIndex];
        
        // Fully illuminate the connection
        this.animateConnectionSegments(edge, weight, activation, progress);
      }
    });
  }
  
  // Helper method to animate nodes in a specific layer
  private animateLayerNodes(layerName: string, activationsMap: Map<string, number[]>, progress: number) {
    if (!activationsMap.has(layerName)) return;
    
    const layerActivations = activationsMap.get(layerName)!;
    const minActivation = Math.min(...layerActivations);
    const maxActivation = Math.max(...layerActivations);
    
    this.nodes.forEach((node, i) => {
      if (node.name === layerName) {
        const layerIndices = this.layerNodeIndices.get(layerName) || [];
        const nodeIndex = layerIndices.indexOf(i);
        
        if (nodeIndex >= 0 && nodeIndex < layerActivations.length) {
          const activation = layerActivations[nodeIndex];
          const gradient = this.currentTrainingData?.gradients?.[i] || 0;
          
          this.updateNodeAppearance(
            node,
            activation,
            gradient,
            minActivation,
            maxActivation,
            progress
          );
        }
      }
    });
  }

  // Animate the connection segments to show signal flowing from left to right
  private animateConnectionSegments(edge: EdgeInfo, weight: number, activation: number, progress: number) {
    const numSegments = edge.segments.length;
    
    // Calculate the "real" activation as the product of weight and activation
    const signalStrength = weight * activation;
    
    // Use the absolute value for brightness, but keep track of sign for color
    const absSignalStrength = Math.abs(signalStrength);
    
    // Scale to reasonable range
    const normalizedStrength = Math.min(absSignalStrength, 1.0);
    
    // Create color based on the sign of the signal
    // Positive: green, Negative: red
    const color = new THREE.Color(signalStrength >= 0 ? 0x00ff00 : 0xff0000);
    
    // Calculate the total physical length of the connection
    const totalLength = edge.fromPosition.distanceTo(edge.toPosition);
    
    // Calculate how far the signal should have traveled based on progress
    // This is now based on physical distance rather than segment count
    const distanceTraveled = progress * totalLength;
    
    // Update each segment based on its physical position
    edge.segments.forEach((segment, i) => {
      const material = segment.material as THREE.LineBasicMaterial;
      
      // Calculate the segment's start and end points
      const segmentLength = totalLength / numSegments;
      const segmentStart = segmentLength * i;
      const segmentEnd = segmentLength * (i + 1);
      
      // If the signal has reached this segment
      if (distanceTraveled > segmentStart) {
        // Calculate how much of this segment should be lit
        const segmentProgress = Math.min(1.0, (distanceTraveled - segmentStart) / segmentLength);
        
        // Segments that should be illuminated
        material.color = color;
        
        // Set opacity based on signal strength and how far the signal has traveled into this segment
        const opacity = (0.1 + normalizedStrength * 0.9) * segmentProgress;
        material.opacity = opacity;
      } else {
        // Segments that should remain unilluminated (gray)
        material.color = new THREE.Color(0x888888);
        material.opacity = 0.2;
      }
    });
  }

  // Apply the final state after animation is complete
  private applyFinalState() {
    const data = this.currentTrainingData;
    if (!data) return;
    
    // Update all nodes based on activations
    const activationsMap = new Map<string, number[]>();
    
    // Collect all activations
    if (data?.activations) {
      Object.entries(data.activations).forEach(([layerName, values]) => {
        if (Array.isArray(values)) {
          activationsMap.set(layerName, values as number[]);
        }
      });
    }
    
    // Update all nodes with full intensity
    this.nodes.forEach((node, i) => {
      const layerName = node.name;
      if (layerName && activationsMap.has(layerName)) {
        const layerActivations = activationsMap.get(layerName)!;
        
        // Find the index of this node within its layer
        const layerIndices = this.layerNodeIndices.get(layerName) || [];
        const nodeIndex = layerIndices.indexOf(i);
        
        if (nodeIndex >= 0 && nodeIndex < layerActivations.length) {
          const activation = layerActivations[nodeIndex];
          
          // Calculate min and max activation values for the layer
          const minActivation = Math.min(...layerActivations);
          const maxActivation = Math.max(...layerActivations);
          
          const gradient = data?.gradients?.[i] || 0;
          this.updateNodeAppearance(node, activation, gradient, minActivation, maxActivation, 1.0);
        }
      }
    });
    
    // Update all edges with full intensity
    if (data?.weights) {
      this.edges.forEach(edge => {
        const [fromLayer, toLayer] = edge.layerConnection.split('-');
        
        // Find the weight matrix for this connection
        const weightKey = `${toLayer}.weight`;
        if (data.weights[weightKey]) {
          // Get the activation of the from node
          const fromActivations = activationsMap.get(fromLayer);
          
          // Find indices within the layer
          const fromLayerIndices = this.layerNodeIndices.get(fromLayer) || [];
          const toLayerIndices = this.layerNodeIndices.get(toLayer) || [];
          
          const fromLayerIndex = fromLayerIndices.indexOf(edge.fromNode);
          const toLayerIndex = toLayerIndices.indexOf(edge.toNode);
          
          if (fromLayerIndex >= 0 && toLayerIndex >= 0 && 
              data.weights[weightKey] && 
              data.weights[weightKey][toLayerIndex] && 
              fromActivations && 
              fromLayerIndex < fromActivations.length) {
            
            // Get the weight value
            const weight = data.weights[weightKey][toLayerIndex][fromLayerIndex];
            // Get the activation value
            const activation = fromActivations[fromLayerIndex];
            
            // For final state, fully illuminate all segments
            const signalStrength = weight * activation;
            const absSignalStrength = Math.abs(signalStrength);
            const normalizedStrength = Math.min(absSignalStrength, 1.0);
            const color = new THREE.Color(signalStrength >= 0 ? 0x00ff00 : 0xff0000);
            
            // Set all segments to fully lit state
            edge.segments.forEach(segment => {
              const material = segment.material as THREE.LineBasicMaterial;
              material.color = color;
              material.opacity = 0.1 + normalizedStrength * 0.9;
            });
          }
        }
      });
    }
  }

  public handleResize() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  public setZoom(zoomLevel: number) {
    this.camera.position.z = 10 * (1 / zoomLevel);
    this.camera.updateProjectionMatrix();
  }

  public setAnimationSpeed(speed: number) {
    this.animationSpeed = speed;
    // Update animation duration - faster speed means shorter duration
    // Use a more balanced calculation to prevent extreme speeds
    // Min duration: 1000ms, Max duration: 8000ms
    this.animationDuration = 8000 / (speed + 0.5);
    
    // Make the end delay shorter and more responsive to speed
    // Min delay: 100ms, Max delay: 500ms
    this.endAnimationDelay = Math.max(100, 600 / (speed + 1));
  }

  public cleanup() {
    this.container.removeChild(this.renderer.domElement);
  }

  private createNode(position: THREE.Vector3): THREE.Mesh {
    const geometry = new THREE.SphereGeometry(0.25, 16, 16); // Larger radius for visibility
    const material = new THREE.MeshStandardMaterial({
      color: 0x4488ff,
      emissive: 0x2244aa,
      roughness: 0.4,
      metalness: 0.5
    });
    const node = new THREE.Mesh(geometry, material);
    node.position.copy(position);
    return node;
  }

  private createEdge(start: THREE.Vector3, end: THREE.Vector3): THREE.Line[] {
    const segments: THREE.Line[] = [];
    const numSegments = 10; // Number of segments to divide the connection into
    
    // Create a series of line segments from start to end
    for (let i = 0; i < numSegments; i++) {
      const t1 = i / numSegments;
      const t2 = (i + 1) / numSegments;
      
      const point1 = new THREE.Vector3(
        start.x + (end.x - start.x) * t1,
        start.y + (end.y - start.y) * t1,
        start.z + (end.z - start.z) * t1
      );
      
      const point2 = new THREE.Vector3(
        start.x + (end.x - start.x) * t2,
        start.y + (end.y - start.y) * t2,
        start.z + (end.z - start.z) * t2
      );
      
      const points = [point1, point2];
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const material = new THREE.LineBasicMaterial({
        color: 0x888888,
        transparent: true,
        opacity: 0.4, // Better visibility
        linewidth: 2
      });

      const segment = new THREE.Line(geometry, material);
      segments.push(segment);
      this.scene.add(segment); // Add each segment to the scene
    }
    
    return segments;
  }

  private updateNodeAppearance(node: THREE.Mesh, activation: number, gradient: number, minActivation: number, maxActivation: number, progress: number = 1.0) {
    const material = node.material as THREE.MeshStandardMaterial;
    const color = new THREE.Color(0x00ff00); // Green color

    // Normalize the activation value to the range 0-1
    const normalizedActivation = (activation - minActivation) / (maxActivation - minActivation);

    // Set a minimum brightness
    let brightness = 0.1;

    // Increase brightness based on normalized activation, scaled by progress
    brightness += Math.min(Math.abs(normalizedActivation), 1) * 0.9 * progress;

    material.emissive = color;
    material.emissiveIntensity = brightness;
  }

  private animate() {
    const currentTime = performance.now();
    
    // Process animation if active
    if (this.isAnimating) {
      this.processAnimation(currentTime);
    }
    
    requestAnimationFrame(() => this.animate());
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  // Reset animation state when training is paused or reset
  private resetAnimationState() {
    // Reset all animation state variables
    this.isAnimating = false;
    this.isWaitingForNextEpoch = false;
    this.currentTrainingData = null;
    
    // Reset all visual elements to default state
    this.resetVisualizationState();
  }
}